import { XMarkIcon } from '@heroicons/react/24/outline';
import type { VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Text } from '../text';
import type { textVariants } from '../text/helpers/variants';

type TagSize = 'default' | 'small';

type TagTextColor = NonNullable<VariantProps<typeof textVariants>['color']>;

type TagProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * タグのラベルテキスト
   */
  children: ReactNode;
  /**
   * タグのサイズ
   */
  size?: TagSize;
  /**
   * ラベルテキストの色（Text コンポーネントの color に渡される）
   *
   * Tag は本来ニュートラル（グレー）ベースだが、リリースタグなどラベル種別を
   * 強調する場面で背景を変える際に文字色も合わせて切り替えるために用意している。
   */
  textColor?: TagTextColor;
  /**
   * タグ本体（ラベル部分）がクリックされた時のコールバック
   */
  onClick?: () => void;
  /**
   * 削除ボタンを表示するか
   */
  onRemove?: (event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => void;
  /**
   * テキスト省略時のツールチップを無効化する（外部でTooltipを制御する場合に使用）
   */
  enabledTooltip?: boolean;
};

/**
 * Tag コンポーネント
 *
 * タグとして使用するコンポーネント。削除ボタン付きのバリエーションをサポート。
 *
 * 注意: このコンポーネントは PopoverTrigger（button）内で使用される場合があるため、
 * 削除ボタンは <button> ではなく <span role="button"> を使用しています。
 * HTML仕様では button > button がネストできないためです。
 */
const SIZE_STYLES: Record<TagSize, string> = {
  default: 'gap-[6px] rounded-[8px] px-[8px] py-[3px]',
  small: 'gap-1 rounded-md px-1.5 py-0.5',
};

export const Tag = ({
  children,
  size = 'default',
  textColor,
  onClick,
  onRemove,
  enabledTooltip = true,
  className,
  ...props
}: TagProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRemove?.(event);
    }
  };

  return (
    <div
      className={cn(
        'inline-flex items-center',
        SIZE_STYLES[size],
        'border border-border-base',
        'bg-surface-strength1',
        className,
      )}
      {...props}
    >
      {onClick ? (
        // biome-ignore lint/a11y/useSemanticElements: PopoverTrigger（button）内で使用されるため span を使用
        <span
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
          className="cursor-pointer"
        >
          <Text
            variant="description"
            color={textColor}
            className={size === 'small' ? 'text-[11px]' : undefined}
          >
            {children}
          </Text>
        </span>
      ) : (
        <Text
          truncate
          enabledTooltip={enabledTooltip}
          variant="description"
          color={textColor}
          className={size === 'small' ? 'text-[11px]' : undefined}
        >
          {children}
        </Text>
      )}
      {onRemove && (
        // biome-ignore lint/a11y/useSemanticElements: PopoverTrigger（button）内で使用されるため、button > button のネストを避けるために span を使用
        <span
          role="button"
          tabIndex={0}
          onClick={onRemove}
          onKeyDown={handleKeyDown}
          className="inline-flex cursor-pointer rounded-full p-0.5 transition-colors hover:bg-foreground/10 focus:outline-none focus:ring-2 focus:ring-focus-ring"
          aria-label="削除"
        >
          <XMarkIcon className={size === 'small' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </span>
      )}
    </div>
  );
};
